import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/services/loggerService';
import { errorReporter } from '@/services/errorReportingService';

interface TrackPlayOptions {
  noteId: string;
  noteOwnerId: string; // kept for backwards-compat, no longer used internally
  playerId?: string; // current user ID (null for shared/anonymous)
  isShared: boolean; // true if accessed via /shared/:token or /public/:token
  shareToken?: string; // share token from /public/:token URL (required for anonymous public plays)
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Fire-and-forget play tracking. Posts to backend which writes with Admin SDK.
 * All errors are swallowed — tracking failures must never break playback.
 */
export const trackVideoPlay = async (options: TrackPlayOptions): Promise<void> => {
  const { noteId, isShared, shareToken } = options;

  const body = {
    is_shared: isShared,
    share_token: shareToken ?? null,
  };

  try {
    const { userToken } = useAuthStore.getState();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (userToken) {
      headers['Authorization'] = `Bearer ${userToken}`;
    }

    const url = `${API_BASE_URL}/notes/${noteId}/track-play`;
    logger.debug('video', 'trackVideoPlay request', { url, body, hasAuth: Boolean(userToken) });

    const response = await window.fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const responseText = await response.text().catch(() => '');

    if (!response.ok) {
      errorReporter.expected(
        `trackVideoPlay non-2xx response: ${response.status}`,
        new Error(`track-play ${response.status}: ${responseText.slice(0, 500)}`),
        {
          feature: 'video',
          operation: 'trackVideoPlay',
          noteId,
          status: String(response.status),
          isShared: String(isShared),
          hasShareToken: String(Boolean(shareToken)),
          hasAuth: String(Boolean(userToken)),
        },
      );
    } else {
      logger.debug('video', 'trackVideoPlay success', { status: response.status, body: responseText.slice(0, 200) });
    }
  } catch (error) {
    // Swallow all errors — fire-and-forget by design.
    errorReporter.expected('trackVideoPlay network/exception failure', error, {
      feature: 'video',
      operation: 'trackVideoPlay',
      noteId,
      isShared: String(isShared),
    });
  }
};

export type { TrackPlayOptions };
